import React, { useRef, useMemo, useEffect } from "react"
import useFbo from "./Fbo"
import { useFrame, useThree } from "react-three-fiber"
import {
  ShaderMaterial,
  AdditiveBlending,
  BufferGeometry,
  BufferAttribute,
  WebGLRenderTarget,
  NearestFilter,
  RGBAFormat,
  LinearFilter,
  Mesh,
  Scene,
  IcosahedronBufferGeometry,
  MeshBasicMaterial,
  OrthographicCamera,
  RepeatWrapping,
  BoxBufferGeometry,
  PlaneBufferGeometry
} from "three"
import distribute from "./distributePoints"

const BUFFER_SIZE = 256

const dummyMesh = new Mesh(
  new IcosahedronBufferGeometry(1.5),
  new MeshBasicMaterial()
)

function getRandomPoints(points) {
  var vertices = new Float32Array(points.length)

  for (var i = 0; i < points.length; i++) {
    var i3 = i * 3
    vertices[i3] = points[i3]
    vertices[i3 + 1] = points[i3 + 1]
    vertices[i3 + 2] = points[i3 + 2]
  }

  return vertices
}

const meshPoints = distribute(dummyMesh, BUFFER_SIZE * BUFFER_SIZE)
const data = getRandomPoints(meshPoints.pos)

class RenderShaderMaterial extends ShaderMaterial {
  constructor(props) {
    super({
      vertexShader: `
        uniform sampler2D positions;
        uniform float pointSize;
        varying float a;
        varying vec2 vUv;

        void main() {
            vec4 col = texture2D( positions, position.xy );
            a = col.a;
            vec3 pos = col.xyz;
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4( pos, 1.0 );
            gl_PointSize = pointSize;
        }
        `,
      fragmentShader: `
        uniform sampler2D positions;
        varying vec2 vUv;
        varying float a;

        void main() {
            gl_FragColor = vec4(vec3(1.0), 0.15);
        }
        `,
      uniforms: {
        positions: { type: "t", value: null },
        pointSize: { type: "f", value: 1 }
      },
      transparent: true,
      blending: AdditiveBlending
    })
  }
}

class ScreenShaderMaterial extends ShaderMaterial {
  constructor(props) {
    super({
      vertexShader: `
          uniform sampler2D screen;
          varying vec2 vUv;
  
          void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
          }
          `,
      fragmentShader: `
          uniform sampler2D screen;
          uniform sampler2D background;
          uniform float opacity;
          varying vec2 vUv;
  
          void main() {
            vec2 uv = vUv;
            float a = opacity;
            uv.y = 1.0-vUv.y;

            vec3 color = texture2D( screen, uv ).rgb;
              float r = texture2D( screen, uv - 0.0015*length(uv-0.5)).r;
              float g = texture2D( screen, uv + 0.0015*length(uv-0.5)).g;
              float b = texture2D( screen, uv - 0.0015*length(uv-0.5)).b;
              
              gl_FragColor = vec4(mix(color, vec3(r,g,b), 0.1), a);

          }
          `,
      uniforms: {
        screen: { type: "t", value: null },
        background: { type: "t", value: null },
        opacity: { value: 0 }
      },
      transparent: true,
      blending: AdditiveBlending
    })
  }
}

export default function FboParticles() {
  const points = useRef()
  const quadMesh = useRef()
  const box = useRef()
  const renderMaterial = useMemo(() => new RenderShaderMaterial(), [])
  const { gl, viewport, size, scene, camera } = useThree()

  const { api: fbo, renderTarget } = useFbo({
    width: BUFFER_SIZE,
    height: BUFFER_SIZE,
    data
  })

  let backgroundFbo = useMemo(
    () =>
      new WebGLRenderTarget(size.width, size.height, {
        minFilter: LinearFilter,
        magFilter: LinearFilter,
        format: RGBAFormat
      }),
    [size]
  )

  let screenFbo = useMemo(
    () =>
      new WebGLRenderTarget(size.width, size.height, {
        minFilter: LinearFilter,
        magFilter: LinearFilter,
        format: RGBAFormat
      }),
    [size]
  )

  const particlesGeometry = useMemo(() => {
    const temp = new BufferGeometry()
    temp.setAttribute("position", new BufferAttribute(data, 3))
    return temp
  }, [])

  const screenMaterial = useMemo(() => new ScreenShaderMaterial(), [])

  let swap = true
  let background = backgroundFbo
  let screen = screenFbo

  useFrame(({ clock }) => {
    // update particles positions
    fbo.update(swap, { renderer: gl, time: clock.getElapsedTime() })

    points.current.material.uniforms.positions.value = renderTarget[0].texture
    //box.current.rotation.y += 0.0035

    quadMesh.current.material.uniforms.screen.value = background.texture
    quadMesh.current.material.uniforms.opacity.value = 0.916

    const prevRenderTarget = gl.getRenderTarget()
    // set screen texture as current target
    gl.setRenderTarget(screen)

    // render the default scene
    gl.render(scene, camera)
    quadMesh.current.material.uniforms.screen.value = screen.texture
    quadMesh.current.material.uniforms.opacity.value = 1

    // set back to main render target
    gl.setRenderTarget(prevRenderTarget)
    gl.render(scene, camera)

    let temp = background
    background = screen
    screen = temp
  }, 1)

  return (
    <group>
      <points ref={points} scale={[1, 1, 1]}>
        <primitive object={particlesGeometry} attach="geometry" />
        <primitive object={renderMaterial} attach="material" />
      </points>
      {/* <mesh ref={box}>
        <icosahedronBufferGeometry args={[1.5]} attach="geometry" />
        <meshBasicMaterial color="orange" attach="material" wireframe />
      </mesh> */}
      <mesh ref={quadMesh}>
        <planeBufferGeometry
          args={[viewport.width, viewport.height]}
          attach="geometry"
        />
        <primitive object={screenMaterial} attach="material" />
      </mesh>
    </group>
  )
}
